import { useState, useEffect } from "react";
import api from "@/lib/api";
import { imgProxy } from "@/lib/img";
import { GENDERS, SEGMENT_LABEL } from "@/lib/segments";
import { ReviewsList } from "@/components/dashboard/Reviews";
import ProfileDetailDialog from "@/components/dashboard/ProfileDetailDialog";
import WhatsAppButton from "@/components/dashboard/WhatsAppButton";
import { Copy, ExternalLink, ChevronLeft, ChevronRight, Phone, MessageSquareQuote, Loader2, Eye, ArrowUpRight } from "lucide-react";

function fmtPhone(p) {
  return p ? p.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3") : "—";
}
function fmtDate(iso) {
  return iso ? new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "short" }) : "—";
}

const GENDER_TONE = { mulher: "#c8aa6e", trans: "#f0abfc", homem: "#7dd3fc" };

function AdRow({ p, onDetail }) {
  return (
    <div className="px-4 py-3 flex items-center gap-3 hover:bg-[#27272a]/40 transition-colors group">
      <button type="button" onClick={() => onDetail(p.id)} className="relative shrink-0" title="Ver detalhe">
        <img src={imgProxy(p.photo_url, 160)} alt="" loading="lazy" decoding="async" className="w-12 h-12 object-cover grayscale-[20%] group-hover:grayscale-0 transition-all bg-[#27272a]" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
      </button>
      <div className="flex-1 min-w-0">
        <button type="button" onClick={() => onDetail(p.id)} className="text-sm text-[#fafafa] truncate hover:text-[#c8aa6e] text-left block max-w-full">{p.name || "—"}</button>
        <div className="text-xs text-[#71717a] flex items-center gap-2 flex-wrap">
          <span className="text-[#a1a1aa]">{p.source_name}</span>
          <span>·</span><span>{p.city || "—"}</span>
          <span>·</span><span>{fmtDate(p.first_seen)}</span>
          {p.segment && <><span>·</span><span>{SEGMENT_LABEL[p.segment] || p.segment}</span></>}
        </div>
      </div>
      <button type="button" onClick={() => onDetail(p.id)} className="hidden sm:inline-flex items-center gap-1 text-xs border border-[#3f3f46] text-[#a1a1aa] hover:border-[#c8aa6e] hover:text-[#c8aa6e] px-2.5 py-1.5" data-testid={`group-detail-${p.id}`}>
        <Eye className="w-3.5 h-3.5" /> Detalhe
      </button>
      <a href={p.profile_url} target="_blank" rel="noreferrer" data-testid={`group-open-${p.id}`}
        className="inline-flex items-center gap-1 text-xs bg-[#c8aa6e] text-black hover:bg-[#d4b982] px-2.5 py-1.5 font-medium whitespace-nowrap">
        Abrir anúncio <ArrowUpRight className="w-3.5 h-3.5" />
      </a>
    </div>
  );
}

export default function PhoneGroups() {
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [byGender, setByGender] = useState({});
  const [gender, setGender] = useState("");
  const [page, setPage] = useState(1);
  const [openPhone, setOpenPhone] = useState(null);
  const [commentsMap, setCommentsMap] = useState({});
  const [loadingPhone, setLoadingPhone] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [loading, setLoading] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    setLoading(true);
    api.get("/phone-groups", { params: { page, page_size: pageSize, gender } }).then(({ data }) => {
      setGroups(data.items);
      setTotal(data.total);
      setByGender(data.by_gender || {});
    }).finally(() => setLoading(false));
  }, [page, gender]);

  const toggleComments = async (phone) => {
    if (openPhone === phone) { setOpenPhone(null); return; }
    setOpenPhone(phone);
    if (commentsMap[phone]) return;
    setLoadingPhone(phone);
    try {
      const { data } = await api.get("/comments/by-phone", { params: { phone } });
      setCommentsMap((m) => ({ ...m, [phone]: data.items || [] }));
    } catch (e) {
      setCommentsMap((m) => ({ ...m, [phone]: [] }));
    } finally {
      setLoadingPhone(null);
    }
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const allTotal = Object.values(byGender).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-4" data-testid="phone-groups">
      <div className="bg-[#18181b] border border-[#27272a] p-5 flex flex-wrap items-center gap-4">
        <div className="w-10 h-10 border border-[#c8aa6e] flex items-center justify-center shrink-0">
          <Copy className="w-4 h-4 text-[#c8aa6e]" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <h3 className="font-head text-lg text-[#fafafa]" data-testid="phone-groups-total">{total} números com múltiplos anúncios</h3>
          <p className="text-sm text-[#71717a]">O número de telefone é o identificador único — perfis com o mesmo número são agrupados e separados por género.</p>
        </div>
        <div className="flex border border-[#27272a]" data-testid="gender-tabs">
          {[{ k: "", label: "Todos" }, ...GENDERS].map((g) => (
            <button key={g.k} data-testid={`gender-tab-${g.k || "all"}`} onClick={() => { setGender(g.k); setPage(1); }}
              className={`px-3 py-2 text-xs whitespace-nowrap transition-colors ${gender === g.k ? "bg-[#c8aa6e] text-black" : "text-[#a1a1aa] hover:text-[#fafafa]"}`}>
              {g.label}<span className="ml-1.5 opacity-70">{g.k ? (byGender[g.k] || 0) : allTotal}</span>
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-sm text-[#71717a] flex items-center gap-2"><Loader2 className="w-4 h-4 spin" /> A carregar…</div>}

      {groups.map((g, gi) => (
        <div key={g.phone} data-testid={`group-${gi}`} className="bg-[#18181b] border border-[#27272a]" style={{ borderLeft: `3px solid ${GENDER_TONE[g.gender] || "#3f3f46"}` }}>
          <div className="px-4 py-3 border-b border-[#27272a] flex items-center gap-3 flex-wrap">
            <Phone className="w-4 h-4 text-[#c8aa6e]" />
            <span className="font-mono text-[#fafafa] text-base" data-testid={`group-phone-${gi}`}>{fmtPhone(g.phone)}</span>
            <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border" style={{ color: GENDER_TONE[g.gender], borderColor: (GENDER_TONE[g.gender] || "#3f3f46") + "66" }} data-testid={`group-gender-${gi}`}>
              {GENDERS.find((x) => x.k === g.gender)?.label || "—"}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <WhatsAppButton phone={g.phone} size={14} showLabel />
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#c8aa6e] border border-[#c8aa6e]/40 px-2 py-1">{g.count} anúncios</span>
            </div>
          </div>
          <div className="divide-y divide-[#27272a]">
            {g.profiles.map((p) => <AdRow key={p.id} p={p} onDetail={setDetailId} />)}
          </div>
          {g.comment_total > 0 && (
            <div className="border-t border-[#27272a]">
              <button type="button" onClick={() => toggleComments(g.phone)} data-testid={`toggle-comments-${gi}`}
                className="w-full px-5 py-2.5 flex items-center gap-2 text-xs text-[#c8aa6e] hover:bg-[#27272a]/30">
                <MessageSquareQuote className="w-3.5 h-3.5" />
                {openPhone === g.phone ? "Ocultar" : "Ver"} {g.comment_total} comentário{g.comment_total > 1 ? "s" : ""} de todos os sites
              </button>
              {openPhone === g.phone && (
                <div className="px-5 pb-4">
                  {loadingPhone === g.phone ? (
                    <div className="flex items-center gap-2 text-xs text-[#71717a] py-2"><Loader2 className="w-3.5 h-3.5 spin" /> A reunir comentários…</div>
                  ) : (
                    <ReviewsList items={commentsMap[g.phone]} showSource empty="Nenhum comentário encontrado." />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {!loading && groups.length === 0 && (
        <div className="bg-[#18181b] border border-[#27272a] px-4 py-12 text-center text-[#71717a] text-sm">
          Ainda não há números duplicados detetados{gender ? " nesta categoria" : ""}.
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm text-[#a1a1aa]">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1.5 border border-[#3f3f46] disabled:opacity-30 hover:border-[#c8aa6e]" data-testid="groups-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="font-mono text-xs">{page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="p-1.5 border border-[#3f3f46] disabled:opacity-30 hover:border-[#c8aa6e]" data-testid="groups-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
      <ProfileDetailDialog profileId={detailId} open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)} />
    </div>
  );
}
